import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/react/shadcn/utils";

const buttonVariants = cva(
  "web3kit-inline-flex web3kit-items-center web3kit-justify-center web3kit-gap-2 web3kit-whitespace-nowrap web3kit-rounded-md web3kit-text-sm web3kit-font-medium web3kit-transition-colors focus-visible:web3kit-outline-none focus-visible:web3kit-ring-1 focus-visible:web3kit-ring-ring disabled:web3kit-pointer-events-none disabled:web3kit-opacity-50 [&_svg]:web3kit-pointer-events-none [&_svg]:web3kit-size-4 [&_svg]:web3kit-shrink-0",
  {
    variants: {
      variant: {
        default:
          "web3kit-bg-primary web3kit-text-primary-foreground web3kit-shadow hover:web3kit-bg-primary/90",
        destructive:
          "web3kit-bg-destructive web3kit-text-destructive-foreground web3kit-shadow-sm hover:web3kit-bg-destructive/90",
        outline:
          "web3kit-border web3kit-border-input web3kit-bg-background web3kit-shadow-sm hover:web3kit-bg-accent hover:web3kit-text-accent-foreground",
        secondary:
          "web3kit-bg-secondary web3kit-text-secondary-foreground web3kit-shadow-sm hover:web3kit-bg-secondary/80",
        ghost: "hover:web3kit-bg-accent hover:web3kit-text-accent-foreground",
        link: "web3kit-text-primary web3kit-underline-offset-4 hover:web3kit-underline",
      },
      size: {
        default: "web3kit-h-9 web3kit-px-4 web3kit-py-2",
        sm: "web3kit-h-8 web3kit-rounded-md web3kit-px-3 web3kit-text-xs",
        lg: "web3kit-h-10 web3kit-rounded-md web3kit-px-8",
        icon: "web3kit-h-9 web3kit-w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    className?: string;
  };

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
